import type { ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { useLoaderData } from 'react-router'
import { ContentError } from '@/components/ContentError'
import { ContentLoading } from '@/components/ContentLoading'
import { useSideSheet } from '@/components/SideSheet'
import { useDocumentTitle } from '@/hooks/document-title'
import { useHotkey } from '@/hooks/keyboard-hooks'
import { useActivation } from '@/hooks/view-model-hooks'
import { OpportunitiesViewHeadingContainer } from '@/features/opportunities/components/OpportunitiesViewHeading/OpportunitiesViewHeadingContainer'
import { type OpportunitiesViewModel } from '@/features/opportunities/OpportunitiesViewModel'
import { type OpportunitiesRootViewModel } from '@/features/opportunities/OpportunitiesRootViewModel'
import { type OpportunitiesViewLoaderData } from '@/features/opportunities/opportunity-route-loaders'
import { OpportunitiesMainView } from '@/features/opportunities/components/Opportunities'
import { OpportunitiesListContainer } from '@/features/opportunities/components/Opportunities/OpportunitiesList/OpportunitiesListContainer'
import { OpportunityDetailsSheetContainer } from '@/features/opportunities/components/OpportunityDetailsSheet/OpportunityDetailsSheetContainer'
import { OverachieverDialogContainer } from '@/features/opportunities/OverachieverDialog/OverachieverDialogContainer'
import { OverachieverBannerContainer } from '@/features/opportunities/components/OverachieverBanner/OverachieverBannerContainer'
import { WorkspaceOpportunitiesMainViewContainer } from '@/features/opportunities/workspace-postings-opportunities/components/WorkspaceOpportunities/WorkspaceOpportunitiesMainViewContainer'
import { OpportunitiesTabBarContainer } from '@/features/opportunities/components/OpportunitiesTabBar/OpportunitiesTabBarContainer'
import { PtinRequiredView } from '@/features/iam/providers/components/PtinRequiredView'
import { match as patternMatch } from '@/utils/pattern-match-util'
import { useFeatures } from '@/contexts/root-store-context'

export default observer(function OpportunitiesContainer() {
  const { vm } = useLoaderData<OpportunitiesViewLoaderData>()
  return <OpportunitiesViewGatekeeperContainer vm={vm} />
})

interface OpportunitiesViewGatekeeperContainerProps {
  vm: OpportunitiesRootViewModel
}

const OpportunitiesViewGatekeeperContainer = observer(
  function OpportunitiesViewGatekeeperContainer({
    vm,
  }: OpportunitiesViewGatekeeperContainerProps) {
    useDocumentTitle('Opportunities')
    useActivation(vm)

    return vm.fetch.match({
      pending: () => <ContentLoading />,
      rejected: (err) => <ContentError error={err} />,
      resolved: () =>
        patternMatch(vm.opportunitiesTabController.current.tab, {
          Jobs: () => {
            return (
              <OpportunitiesViewContainer
                vm={vm.opportunitiesVm}
                tabBars={
                  vm.showTabs && (
                    <OpportunitiesTabBarContainer
                      tabsController={vm.opportunitiesTabController}
                    />
                  )
                }
              />
            )
          },
          Workspaces: () => (
            <WorkspaceOpportunitiesMainViewContainer
              vm={vm.workspaceOpportunitiesVm}
              tabBars={
                vm.showTabs && (
                  <OpportunitiesTabBarContainer
                    tabsController={vm.opportunitiesTabController}
                  />
                )
              }
            />
          ),
        }),
    })
  },
)

interface OpportunitiesViewContainerProps {
  vm: OpportunitiesViewModel
  tabBars?: ReactNode
}

const OpportunitiesViewContainer = observer(
  function OpportunitiesViewContainer({
    vm,
    tabBars,
  }: OpportunitiesViewContainerProps) {
    useActivation(vm)
    const searchState = vm.searcher.searchState
    if (searchState === 'InitialSearchRunning') {
      return <ContentLoading />
    }

    return <OpportunitiesMainViewContainer vm={vm} tabBars={tabBars} />
  },
)

interface OpportunitiesMainViewContainerProps {
  vm: OpportunitiesViewModel
  tabBars?: ReactNode
}

const OpportunitiesMainViewContainer = observer(
  function OpportunitiesMainViewContainer({
    vm,
    tabBars,
  }: OpportunitiesMainViewContainerProps) {
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))
    const sideSheet = useSideSheet()
    useHotkey(['Escape'], vm.searcher.searchText.clear)

    if (vm.ptinRequired) {
      return <PtinRequiredView corporateMode={shellInfo.corporateMode} />
    }

    return (
      <OpportunitiesMainView
        tabBars={tabBars}
        sheetElevation={sideSheet.elevation}
        sheetVisible={!!vm.detailsVm.selected || !sideSheet.shouldBeAbove}
        onRequestHideSheet={vm.detailsVm.deselect}
        heading={
          <OpportunitiesViewHeadingContainer
            vm={vm.searcher}
            viewTitle="Opportunities"
            allOptionText="All Opportunities"
            workspaces={vm.workspacesWhereUserCanViewOpportunities}
          />
        }
        searchText={vm.searcher.searchText.value}
        onSearchTextChange={vm.searcher.searchText.change}
        banner={
          vm.showOverachieverBanner && <OverachieverBannerContainer vm={vm} />
        }
        resultCount={vm.opportunities.length}
        list={<OpportunitiesListContainer vm={vm} />}
        detailsSheet={
          vm.canPickUp && (
            <OpportunityDetailsSheetContainer
              vm={vm.detailsVm}
              // Only show the close button when in overlay mode
              onClose={
                sideSheet.elevation === 'above'
                  ? vm.detailsVm.deselect
                  : undefined
              }
            />
          )
        }
        overachieverModal={
          <OverachieverDialogContainer vm={vm.overachieverModalVm} />
        }
      />
    )
  },
)
